/* eslint-disable no-case-declarations */
/* eslint-disable react/no-danger */
import React, { useState, lazy, Suspense } from "react";
import PropTypes from "prop-types";
// eslint-disable-next-line import/no-extraneous-dependencies
import ScrollToTop from "~atoms/scroll-to-top";
import CookieConsent from "~organisms/cookie-consent";
import isSticky from "~hooks/stickyOnScroll";
import { TrackingKeys } from "~constants/tracking-api";
import { mainSettings } from "../../../constants/site-settings/main";
import styles from "./floating-area.module.scss";
import { pageTypes } from "../../../constants/site-settings/navigation";
import { trackerLinkActive } from "~helpers/tracker.mjs";

const showOperatorBanner = (operator, pageTemplate) =>
  mainSettings[pageTemplate]?.operator_banner &&
  trackerLinkActive(operator) &&
  !["not_recommended", "inactive", "blacklisted", "coming_soon"].includes(
    operator.status
  );

export default function FloatingArea({
  pageContext,
  template,
  offsetTop = 400,
  showCookies=true,
  customCookieContent
}) {
  const showScroll = isSticky(offsetTop);
  const [closedBanner, setClosedBanner] = useState(false);

  const pageType = pageContext.page.relation_type;
  const pageTemplate = pageContext?.page?.template;

  const getAllMarketsHomepage = () =>
    Object.keys(pageContext?.allMarkets || {})?.map(
      (elm) => elm.split("_")[1]
    ) || [];

  const isPageHomepage =
    getAllMarketsHomepage()?.includes(pageContext?.page?.path) ||
    pageContext?.page?.path === "/";

  const footerNavigationData =
    pageContext.marketSections?.footer_navigation || null;

  const operatorData =
    // eslint-disable-next-line no-unused-expressions
    pageType === "operator"
      ? pageContext?.page?.relation
      : pageContext?.page?.sections?.header?.extra_fields?.operator_banner;

  const OperatorBanner =
    operatorData && showOperatorBanner(operatorData, pageTemplate)
      ? lazy(() =>
          import(
            `gatsby-core-theme/src/components/molecules/bonus/template-two`
          )
        )
      : null;

  const FooterNavigation =
    (pageTypes?.[pageTemplate]?.footerNavigation ||
    pageTypes.default?.footerNavigation) &&
    footerNavigationData &&
    footerNavigationData?.modules?.length > 0
      ? lazy(() =>
          import(`gatsby-core-theme/src/components/organisms/footer-navigation`)
        )
      : null;

  return (
    <div className={`${styles.floatingArea} ${showScroll ? styles.show : ""}`}>
      {pageTypes?.[pageTemplate]?.showScrollToTop || pageTypes.default?.showScrollToTop && <ScrollToTop />}
      {OperatorBanner && (
        <Suspense fallback={<></>}>
          <OperatorBanner
            type={pageType}
            pageTemplate={template}
            moduleName={TrackingKeys?.BONUS}
            sticky
            logo={pageContext?.page?.relation?.logo_url}
            operator={operatorData}
            closedBanner={closedBanner}
            setClosedBanner={setClosedBanner}
          />
        </Suspense>
      )}

      {CookieConsent &&
        showCookies && (
          <CookieConsent
            market={pageContext?.page?.market}
            type={pageType}
            template={template}
            settingsCookie
            isPageHomepage={isPageHomepage}
          >
            {
              customCookieContent || <p>
                We use cookies in order to optimise our site and improve your
                experience with us. By using the site you consent to our
                <a href="/cookies" className="cookie-consent-gtm">
                  Cookie Policy
                </a>
                .
              </p>
            }
          </CookieConsent>
        )}
      {FooterNavigation && (
        <Suspense fallback={<></>}>
          <FooterNavigation section={footerNavigationData} />
        </Suspense>
      )}
    </div>
  );
}

FloatingArea.propTypes = {
  pageContext: PropTypes.shape({
    allMarkets: PropTypes.shape({}),
    page: PropTypes.shape({
      id: PropTypes.number,
      relation_type: PropTypes.string,
      path: PropTypes.string,
      template: PropTypes.string,
      relation: PropTypes.shape({
        status: PropTypes.string,
        logo_url: PropTypes.string,
      }),
      market: PropTypes.string,
      sections: PropTypes.shape({
        header: PropTypes.shape({
          extra_fields: PropTypes.shape({
            operator_banner: PropTypes.shape({}),
          }),
        }),
      }),
    }),
    marketSections: PropTypes.shape({
      footer_navigation: PropTypes.shape({}),
      popup: PropTypes.shape({
        modules: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    }),
  }).isRequired,
  offsetTop: PropTypes.number,
  template: PropTypes.string,
  showCookies: PropTypes.bool,
  customCookieContent: PropTypes.node
};
